/**
 * R232q5aM: Macros store — WoW WotLK 3.3.5a parity.
 *
 * Two tabs / 36 slots total:
 *   - 18 «account» macros (player-wide, shared across all alts)
 *   - 18 «character» macros (this character only)
 *
 * Each slot holds {name, icon, body, hotkey?}. Body is the multi-line
 * command source the macro engine parses (see lib/macro-engine.ts).
 *
 * Persistence:
 *   - Always written to localStorage under `aiwari-macros-v2`.
 *   - On change we also fire an async POST to /aiwari-api/macros/save
 *     (best-effort, no UI feedback). Server route is added in Wave 3a #5.
 *   - On boot we fetch /aiwari-api/macros/load/:stormId/:name and merge
 *     server character-macros over local ones (server wins).
 */

import { create } from 'zustand';

const STORAGE_KEY = 'aiwari-macros-v2';
export const PLAYER_MACROS = 18;
export const CHARACTER_MACROS = 18;

const API_BASE = import.meta.env.DEV ? 'http://localhost:2570' : '/aiwari-api';

export interface MacroSlot {
  /** Slot index 0..17. */
  idx: number;
  /** Display name shown on the action bar tooltip. */
  name: string;
  /** Icon path or emoji ('⚔️', '🛡', or '/icons/abilities/cleave.png'). */
  icon: string;
  /** Multi-line macro body. */
  body: string;
  /** Optional hotkey string, e.g. 'Shift+1'. Empty = no hotkey. */
  hotkey: string;
}

export type MacroScope = 'player' | 'character';

interface MacrosStore {
  player: MacroSlot[];   // length PLAYER_MACROS
  character: MacroSlot[]; // length CHARACTER_MACROS
  /** Update a slot. Empty body deletes the slot (sets to defaults). */
  update: (scope: MacroScope, idx: number, patch: Partial<MacroSlot>) => void;
  /** Wipe a slot back to default empty. */
  clear: (scope: MacroScope, idx: number) => void;
  /** Find by hotkey across both scopes (character first → player). */
  findByHotkey: (hotkey: string) => { scope: MacroScope; slot: MacroSlot } | null;
  /** Serialise + POST to server. Best-effort — silent failure. */
  syncToServer: (stormId: string, name: string) => Promise<void>;
  /** Fetch from server and merge over local. */
  loadFromServer: (stormId: string, name: string) => Promise<void>;
}

function makeEmpty(idx: number): MacroSlot {
  return { idx, name: '', icon: '', body: '', hotkey: '' };
}

function emptyRow(n: number): MacroSlot[] {
  return Array.from({ length: n }, (_, i) => makeEmpty(i));
}

interface Persisted {
  player: MacroSlot[];
  character: MacroSlot[];
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { player: emptyRow(PLAYER_MACROS), character: emptyRow(CHARACTER_MACROS) };
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    const fix = (arr: unknown, n: number): MacroSlot[] => {
      const out = emptyRow(n);
      if (!Array.isArray(arr)) return out;
      for (const item of arr as Array<Partial<MacroSlot>>) {
        const i = typeof item?.idx === 'number' ? item.idx : -1;
        if (i < 0 || i >= n) continue;
        out[i] = {
          idx: i,
          name: typeof item.name === 'string' ? item.name : '',
          icon: typeof item.icon === 'string' ? item.icon : '',
          body: typeof item.body === 'string' ? item.body : '',
          hotkey: typeof item.hotkey === 'string' ? item.hotkey : '',
        };
      }
      return out;
    };
    return {
      player: fix(parsed.player, PLAYER_MACROS),
      character: fix(parsed.character, CHARACTER_MACROS),
    };
  } catch {
    return { player: emptyRow(PLAYER_MACROS), character: emptyRow(CHARACTER_MACROS) };
  }
}

function save(state: Persisted) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

export const useMacros = create<MacrosStore>((set, get) => ({
  ...load(),
  update: (scope, idx, patch) => {
    const cur = get();
    const arr = scope === 'player' ? [...cur.player] : [...cur.character];
    if (idx < 0 || idx >= arr.length) return;
    arr[idx] = { ...arr[idx]!, ...patch, idx };
    const next: Persisted = scope === 'player'
      ? { player: arr, character: cur.character }
      : { player: cur.player, character: arr };
    save(next);
    set(next);
  },
  clear: (scope, idx) => {
    get().update(scope, idx, { name: '', icon: '', body: '', hotkey: '' });
  },
  findByHotkey: (hotkey) => {
    if (!hotkey) return null;
    const norm = hotkey.toUpperCase();
    const cur = get();
    for (const slot of cur.character) {
      if (slot.hotkey && slot.hotkey.toUpperCase() === norm) return { scope: 'character', slot };
    }
    for (const slot of cur.player) {
      if (slot.hotkey && slot.hotkey.toUpperCase() === norm) return { scope: 'player', slot };
    }
    return null;
  },
  syncToServer: async (stormId, name) => {
    if (!stormId || !name) return;
    if (!/^[0-9a-fA-F-]{36}$/.test(stormId)) return; // guests skip sync
    const cur = get();
    try {
      await fetch(`${API_BASE}/macros/save`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          stormId, name,
          player: cur.player,
          character: cur.character,
        }),
      });
    } catch { /* best-effort */ }
  },
  loadFromServer: async (stormId, name) => {
    if (!stormId || !name) return;
    if (!/^[0-9a-fA-F-]{36}$/.test(stormId)) return;
    try {
      const res = await fetch(`${API_BASE}/macros/load/${stormId}/${encodeURIComponent(name)}`);
      if (!res.ok) return;
      const json = await res.json() as { player?: MacroSlot[]; character?: MacroSlot[] };
      const cur = get();
      const next: Persisted = {
        player: Array.isArray(json.player) ? mergeRow(cur.player, json.player, PLAYER_MACROS) : cur.player,
        character: Array.isArray(json.character) ? mergeRow(cur.character, json.character, CHARACTER_MACROS) : cur.character,
      };
      save(next);
      set(next);
    } catch { /* offline */ }
  },
}));

function mergeRow(local: MacroSlot[], remote: Partial<MacroSlot>[], n: number): MacroSlot[] {
  const out = local.slice(0, n);
  while (out.length < n) out.push(makeEmpty(out.length));
  for (const r of remote) {
    if (!r || typeof r.idx !== 'number') continue;
    if (r.idx < 0 || r.idx >= n) continue;
    out[r.idx] = {
      idx: r.idx,
      name: typeof r.name === 'string' ? r.name : '',
      icon: typeof r.icon === 'string' ? r.icon : '',
      body: typeof r.body === 'string' ? r.body : '',
      hotkey: typeof r.hotkey === 'string' ? r.hotkey : '',
    };
  }
  return out;
}

/** Drag-mime for macro slot → action bar. Payload: {scope, idx}. */
export const MACRO_DND_MIME = 'application/x-aiwari-macro';
